import * as styles from "./Tour.css";

import type { JSX } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  PopoverDescription,
  PopoverTitle,
} from "@frontend/components/atoms/Popover";
import { useLingui } from "@lingui/react/macro";

import type { TourStep } from "./Tour.types";

type TourCardProps = {
  step: TourStep;
  index: number;
  total: number;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
};

export function TourCard({
  step,
  index,
  total,
  onNext,
  onPrevious,
  onSkip,
}: TourCardProps): JSX.Element {
  const { t } = useLingui();

  const isLast = index === total - 1;

  return (
    <>
      <div className={styles.header}>
        {step.icon && (
          <Icon name={step.icon} size="sm" className={styles.icon} />
        )}

        <PopoverTitle className={styles.title}>{step.title}</PopoverTitle>

        <Button
          variant="ghost"
          size="sm"
          aria-label={t`Skip the tour`}
          onClick={onSkip}
        >
          <Icon name="X" size="sm" />
        </Button>
      </div>

      <PopoverDescription render={<div />} className={styles.content}>
        {step.content}
      </PopoverDescription>

      {step.keys && step.keys.length > 0 && (
        <div className={styles.keys}>
          {step.keys.map((label) => (
            <kbd key={label} className={styles.key}>
              {label}
            </kbd>
          ))}
        </div>
      )}

      <div className={styles.footer}>
        <div
          className={styles.progress}
          aria-label={t`Step ${index + 1} of ${total}`}
        >
          {Array.from({ length: total }, (_, position) => (
            <span
              key={position}
              className={styles.dot({ current: position === index })}
            />
          ))}
        </div>

        <div className={styles.actions}>
          {index > 0 && (
            <Button variant="ghost" size="sm" onClick={onPrevious}>
              {t`Back`}
            </Button>
          )}

          <Button variant="primary" size="sm" onClick={onNext}>
            {isLast ? t`Done` : t`Next`}
          </Button>
        </div>
      </div>
    </>
  );
}
